import { createContext, use } from "react";

/**
 * The seeded role the demo persona switcher starts on.
 *
 * Administrator, because that is the real role of the fixture's one identity
 * (`usr_0001` in `fixtures/team.ts`) — opening the app for the first time
 * should show everything, and narrowing to another persona is something a
 * demo walkthrough opts into.
 */
export const DEFAULT_ROLE_ID = "role_admin";

const SESSION_STORAGE_KEY = "estelle.session";

interface StoredSession {
  activeRoleId: string;
}

function isStoredSession(value: unknown): value is StoredSession {
  return (
    typeof value === "object" &&
    value !== null &&
    "activeRoleId" in value &&
    typeof value.activeRoleId === "string"
  );
}

/**
 * Reads the session record straight from storage.
 *
 * Wrapped in try/catch because `sessionStorage` throws outright in a browser
 * configured to block site data — not returning null, throwing — and a demo
 * sign-in should not white-screen over that.
 */
export function readStoredSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isStoredSession(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeStoredSession(session: StoredSession): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // A session that cannot be persisted still works for the rest of this
    // tab's life; only a reload loses it, which is the honest fallback.
  }
}

export function clearStoredSession(): void {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Nothing to undo if the store never accepted the write in the first
    // place.
  }
}

/**
 * Whether the demo session record exists.
 *
 * Only consulted when the app runs on fixtures, which is what
 * `auth/backend.ts` decides; against the API the server's answer is the one
 * that counts.
 */
export function hasStoredSession(): boolean {
  return readStoredSession() !== null;
}

export interface SessionValue {
  /**
   * The seeded role driving the shell right now. Demo-only (#135, #189): on
   * fixtures it is a fixture role id; against the API it is the empty string,
   * because there is no persona there — read the signed-in user's roles from
   * `CurrentUser.roleNames` instead (#429).
   */
  activeRoleId: string;
  /**
   * Checks the credentials with the API and, on success, leaves this browser
   * holding a session cookie. Rejects with the message to show.
   *
   * Declared as arrow-typed properties rather than interface methods on
   * purpose: a method signature lets `this` float free, and every consumer
   * here destructures these off the context value and calls them
   * unbound — exactly what `@typescript-eslint/unbound-method` exists to
   * catch.
   */
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  setActiveRoleId: (roleId: string) => void;
}

export const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const value = use(SessionContext);
  if (!value) {
    throw new Error("useSession used outside SessionProvider");
  }
  return value;
}
